// === Full-Fledged GIS Editor Component with OpenLayers ===
import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Fill, Stroke, Circle, Text } from 'ol/style';
import { Draw, Modify, Snap } from 'ol/interaction';
import { Feature } from 'ol';
import { Point, LineString, Polygon } from 'ol/geom';
import { defaults as defaultControls, Zoom, Attribution } from 'ol/control';
import { getArea, getLength } from 'ol/sphere';
import { unByKey } from 'ol/Observable';
import { EventsKey } from 'ol/events';
import { Overlay } from 'ol';
import { getCenter } from 'ol/extent';
import { Subject, takeUntil } from 'rxjs';

// Drawing modes
enum DrawingMode {
  SELECT = 'select',
  POINT = 'point',
  LINE = 'line',
  POLYGON = 'polygon',
  TEXT = 'text'
}

// Text formatting options
interface TextFormatting {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  fontSize: number;
}

// Construction Node interface
interface ConstructionNode {
  id: string;
  name: string;
  type: 'start' | 'end' | 'milestone' | 'checkpoint' | 'junction' | 'bridge' | 'tunnel' | 'custom';
  coordinates: number[]; // [longitude, latitude]
  properties: {
    elevation?: number;
    status: 'planned' | 'in-progress' | 'completed' | 'blocked' | 'cancelled';
    priority: 'low' | 'medium' | 'high' | 'critical';
    assignedTo?: string;
    estimatedDuration?: number; // in days
    actualDuration?: number; // in days
    startDate?: Date;
    endDate?: Date;
    materials?: string[];
    equipment?: string[];
    notes?: string;
    attachments?: string[];
    qualityCheck?: boolean;
    safetyCheck?: boolean;
    createdDate: Date;
    modifiedDate: Date;
  };
  isSelected: boolean;
  isVisible: boolean;
}

// Construction Phase interface
interface ConstructionPhase {
  id: string;
  name: string;
  description: string;
  order: number;
  status: 'pending' | 'in-progress' | 'completed' | 'on-hold';
  startDate?: Date;
  endDate?: Date;
  estimatedCost?: number;
  actualCost?: number;
  estimatedDuration?: number; // in days
  actualDuration?: number; // in days
  progress: number; // 0-100
  dependencies: string[]; // IDs of phases this depends on
  deliverables: string[];
  qualityStandards: string[];
  safetyRequirements: string[];
}

// Enhanced Segment management interface for construction
interface SegmentInfo {
  id: string;
  name: string;
  prefix: string;
  suffix: string;
  comments: string;
  subSegments: SegmentInfo[];
  geometry?: any; // OpenLayers geometry
  properties: {
    length?: number;
    area?: number;
    startPoint?: number[];
    endPoint?: number[];
    roadType?: string;
    condition?: string;
    createdDate: Date;
    modifiedDate: Date;
  };
  isSelected: boolean;
  isVisible: boolean;
  // Construction-specific properties
  construction?: {
    type: 'road' | 'bridge' | 'tunnel' | 'building' | 'utility' | 'landscape' | 'drainage' | 'custom';
    status: 'planning' | 'design' | 'preparation' | 'construction' | 'testing' | 'completed' | 'maintenance';
    priority: 'low' | 'medium' | 'high' | 'critical';
    estimatedCost: number;
    actualCost: number;
    estimatedDuration: number; // in days
    actualDuration: number; // in days
    startDate?: Date;
    endDate?: Date;
    progress: number; // 0-100
    contractor?: string;
    supervisor?: string;
    qualityInspector?: string;
    safetyOfficer?: string;
    materials: string[];
    equipment: string[];
    phases: ConstructionPhase[];
    nodes: ConstructionNode[];
    qualityStandards: string[];
    safetyRequirements: string[];
    environmentalImpact?: string;
    permits: string[];
    inspections: {
      date: Date;
      type: string;
      inspector: string;
      status: 'passed' | 'failed' | 'pending';
      notes: string;
    }[];
    risks: {
      id: string;
      description: string;
      probability: 'low' | 'medium' | 'high';
      impact: 'low' | 'medium' | 'high';
      mitigation: string;
      status: 'open' | 'mitigated' | 'closed';
    }[];
    documents: {
      id: string;
      name: string;
      type: 'drawing' | 'specification' | 'permit' | 'inspection' | 'photo' | 'report';
      url: string;
      uploadedDate: Date;
      uploadedBy: string;
    }[];
  };
}

@Component({
  selector: 'app-map-editor',
  templateUrl: './mapeditor.component.html',
  styleUrls: ['./mapeditor.component.css']
})
export class MapeditorComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  @ViewChild('popup', { static: true }) popup!: ElementRef;

  // Map and layers
  map!: Map;
  vectorSource!: VectorSource;
  vectorLayer!: VectorLayer<VectorSource>;
  
  // Drawing interactions
  draw!: Draw;
  modify!: Modify;
  snap!: Snap;
  
  // Current state
  currentDrawingMode: DrawingMode = DrawingMode.SELECT;
  isDrawing = false;
  
  // Drawing styles
  lineColor = '#007cbf';
  lineWidth = 2;
  symbolSize = 1.0;
  textContent = 'Enter text';
  textColor = '#000000';
  textFormatting: TextFormatting = {
    bold: false,
    italic: false,
    underline: false,
    fontSize: 14
  };
  
  // Available colors
  lineColors = [
    '#007cbf', '#ff0000', '#00ff00', '#ffa500', 
    '#800080', '#00ffff', '#808080', '#000000'
  ];
  
  textColors = [
    '#000000', '#ffffff', '#ff0000', '#00ff00', 
    '#ffa500', '#800080', '#c0c0c0', '#808080'
  ];
  
  // Project and settings
  selectedProject = 'NH-44 Delhi-Mumbai';
  projects = [
    'NH-44 Delhi-Mumbai',
    'NH-48 Mumbai-Bangalore',
    'NH-65 Hyderabad-Vijayawada',
    'NH-16 Chennai-Visakhapatnam'
  ];

  // Enhanced Segment Management
  comments = '';
  numberingType = 'auto';
  segmentPrefix = 'SEG-';
  segmentSuffix = '';
  currentSegment = 'SEG-1-A';
  segments: SegmentInfo[] = [];
  currentSegmentIndex = 0;
  selectedSegment: SegmentInfo | null = null;
  segmentCounter = 1;
  subSegmentCounter = 1;
  
  // Segment drawing mode
  isSegmentDrawing = false;
  segmentDrawingMode: 'line' | 'polygon' = 'line';
  currentSegmentPoints: number[][] = [];

  // Construction Management Properties
  showConstructionManager = false;
  selectedConstructionSegment: SegmentInfo | null = null;
  selectedNode: ConstructionNode | null = null;
  selectedPhase: ConstructionPhase | null = null;
  
  // Construction Node Management
  isNodeDrawing = false;
  nodeTypes = ['start', 'end', 'milestone', 'checkpoint', 'junction', 'bridge', 'tunnel', 'custom'];
  selectedNodeType: string = 'milestone';
  
  // Construction Phase Management
  phaseStatuses = ['pending', 'in-progress', 'completed', 'on-hold'];
  constructionTypes = ['road', 'bridge', 'tunnel', 'building', 'utility', 'landscape', 'drainage', 'custom'];
  constructionStatuses = ['planning', 'design', 'preparation', 'construction', 'testing', 'completed', 'maintenance'];
  priorities = ['low', 'medium', 'high', 'critical'];
  
  // Construction Forms
  newNodeForm = {
    name: '',
    type: 'milestone',
    notes: '',
    assignedTo: '',
    estimatedDuration: 1,
    priority: 'medium'
  };
  
  newPhaseForm = {
    name: '',
    description: '',
    estimatedCost: 0,
    estimatedDuration: 1,
    deliverables: '',
    qualityStandards: '',
    safetyRequirements: ''
  };
  
  constructionForm = {
    type: 'road',
    status: 'planning',
    priority: 'medium',
    estimatedCost: 0,
    estimatedDuration: 30,
    contractor: '',
    supervisor: '',
    qualityInspector: '',
    safetyOfficer: '',
    materials: '',
    equipment: '',
    qualityStandards: '',
    safetyRequirements: '',
    environmentalImpact: '',
    permits: ''
  };
  
  // Coordinate display
  coordinates = { lat: 23.2599, lon: 77.4146 };
  scale = '1:100000';
  zoom = 10;
  
  // Measurement
  measureOverlay!: Overlay;
  sketch!: Feature;
  helpTooltipElement!: HTMLElement;
  measureTooltipElement!: HTMLElement;
  measureTooltip!: Overlay;
  helpTooltip!: Overlay;
  
  // History
  history: any[] = [];
  historyIndex = -1;
  
  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();
  private listenerKeys: EventsKey[] = [];

  constructor() {}

  ngOnInit(): void {
    // Initialize any required services or data
  }

  ngAfterViewInit(): void {
    this.initializeMap();
    this.setupDrawingInteractions();
    this.setupMeasurement();
    this.setupCoordinateDisplay();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up event listeners
    this.listenerKeys.forEach(key => unByKey(key));
  }

  private initializeMap(): void {
    // Create vector source and layer
    this.vectorSource = new VectorSource();
    this.vectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: this.getDefaultStyle()
    });

    // Create map
    this.map = new Map({
      target: this.mapContainer.nativeElement,
      layers: [
        new TileLayer({
          source: new OSM()
        }),
        this.vectorLayer
      ],
      view: new View({
        center: fromLonLat([77.4146, 23.2599]), // Bhopal coordinates
        zoom: 10
      }),
      controls: defaultControls({
        zoom: true,
        attribution: true
      }).extend([
        new Zoom({
          zoomInLabel: '+',
          zoomOutLabel: '-'
        })
      ])
    });

    // Create popup overlay
    const popup = new Overlay({
      element: this.popup.nativeElement,
      positioning: 'bottom-center',
      stopEvent: false,
      offset: [0, -50]
    });
    this.map.addOverlay(popup);
  }

  private setupDrawingInteractions(): void {
    // Create modify interaction
    this.modify = new Modify({ 
      source: this.vectorSource,
      style: new Style({
        image: new Circle({
          radius: 5,
          fill: new Fill({
            color: '#ff0000'
          }),
          stroke: new Stroke({
            color: '#ffffff',
            width: 2
          })
        })
      })
    });
    this.map.addInteraction(this.modify);

    // Create snap interaction
    this.snap = new Snap({ source: this.vectorSource });
    this.map.addInteraction(this.snap);

    // Initially disable modify
    this.modify.setActive(false);
  }

  private setupMeasurement(): void {
    // Create measure tooltip
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center'
    });
    this.map.addOverlay(this.measureTooltip);

    // Create help tooltip
    this.helpTooltipElement = document.createElement('div');
    this.helpTooltipElement.className = 'ol-tooltip ol-tooltip-static';
    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left'
    });
    this.map.addOverlay(this.helpTooltip);
  }

  private setupCoordinateDisplay(): void {
    // Update coordinates on pointer move
    const key = this.map.on('pointermove', (evt) => {
      const coordinate = evt.coordinate;
      const lonLat = toLonLat(coordinate);
      this.coordinates = {
        lat: parseFloat(lonLat[1].toFixed(6)),
        lon: parseFloat(lonLat[0].toFixed(6))
      };
    });
    this.listenerKeys.push(key);

    // Update zoom and scale
    const view = this.map.getView();
    const key2 = view.on('change:resolution', () => {
      this.zoom = Math.round(view.getZoom() || 0);
      const resolution = view.getResolution() || 0;
      this.scale = this.calculateScale(resolution);
      console.log('Scale calculation - Resolution:', resolution, 'Scale:', this.scale);
    });
    this.listenerKeys.push(key2);
  }

  private calculateScale(resolution: number): string {
    // More accurate scale calculation for OpenLayers
    // Scale = Resolution * DPI * Inches per meter
    const dpi = 96; // Standard screen DPI
    const inchesPerMeter = 39.3701; // Inches per meter
    const scale = resolution * dpi * inchesPerMeter;
    
    // Round to nearest reasonable scale
    let roundedScale: number;
    
    if (scale < 1) {
      // For very large scales (zoomed in)
      roundedScale = Math.round(1 / scale);
      return `1:${roundedScale.toLocaleString()}`;
    } else if (scale < 10) {
      // For large scales
      roundedScale = Math.round(scale);
      return `1:${roundedScale.toLocaleString()}`;
    } else if (scale < 100) {
      // For medium scales
      roundedScale = Math.round(scale / 10) * 10;
      return `1:${roundedScale.toLocaleString()}`;
    } else if (scale < 1000) {
      // For small scales
      roundedScale = Math.round(scale / 100) * 100;
      return `1:${roundedScale.toLocaleString()}`;
    } else if (scale < 10000) {
      // For very small scales
      roundedScale = Math.round(scale / 1000) * 1000;
      return `1:${(roundedScale / 1000).toFixed(1)}K`;
    } else {
      // For extremely small scales
      roundedScale = Math.round(scale / 10000) * 10000;
      return `1:${(roundedScale / 1000000).toFixed(1)}M`;
    }
  }

  // Drawing mode methods
  setDrawingMode(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const mode = target.value as DrawingMode;
    this.setDrawingModeInternal(mode);
  }

  private setDrawingModeInternal(mode: DrawingMode): void {
    this.currentDrawingMode = mode;
    this.isDrawing = mode !== DrawingMode.SELECT;
    
    // Remove existing draw interaction
    if (this.draw) {
      this.map.removeInteraction(this.draw);
    }
    
    // Clear any existing text drawing listeners
    this.clearTextDrawingListeners();
    
    // Disable modify when drawing
    this.modify.setActive(mode === DrawingMode.SELECT);

    if (mode === DrawingMode.SELECT) {
      return;
    }

    // Create new draw interaction based on mode
    let geometryType: any;
    switch (mode) {
      case DrawingMode.POINT:
        geometryType = 'Point';
        break;
      case DrawingMode.LINE:
        geometryType = 'LineString';
        break;
      case DrawingMode.POLYGON:
        geometryType = 'Polygon';
        break;
      case DrawingMode.TEXT:
        this.setupTextDrawing();
        return;
      default:
        return;
    }

    this.draw = new Draw({
      source: this.vectorSource,
      type: geometryType,
      style: this.getDrawingStyle()
    });

    this.map.addInteraction(this.draw);

    // Handle draw events
    this.draw.on('drawstart', (evt) => {
      this.sketch = evt.feature;
      console.log('Drawing started:', geometryType);
    });

    this.draw.on('drawend', (evt) => {
      const feature = evt.feature;
      console.log('Drawing ended:', feature);
      // Apply current styling to the drawn feature
      feature.setStyle(this.getFeatureStyle(feature.getGeometry()));
      this.addToHistory();
    });
  }

  // Style methods
  private getDefaultStyle(): Style {
    return new Style({
      fill: new Fill({
        color: 'rgba(0, 124, 191, 0.3)'
      }),
      stroke: new Stroke({
        color: this.lineColor,
        width: this.lineWidth
      }),
      image: new Circle({
        radius: 5 * this.symbolSize,
        fill: new Fill({
          color: this.lineColor
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  private getDrawingStyle(): Style {
    // Style for features being drawn (semi-transparent)
    return new Style({
      fill: new Fill({
        color: 'rgba(0, 124, 191, 0.2)'
      }),
      stroke: new Stroke({
        color: this.lineColor,
        width: this.lineWidth,
        lineDash: [5, 5]
      }),
      image: new Circle({
        radius: 5 * this.symbolSize,
        fill: new Fill({
          color: this.lineColor
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      })
    });
  }

  // Color and style setters
  setLineColor(color: string): void {
    this.lineColor = color;
    this.updateLayerStyle();
  }

  setLineWidth(event: Event): void {
    const target = event.target as HTMLInputElement;
    const width = +target.value;
    this.lineWidth = width;
    this.updateLayerStyle();
  }

  setSymbolSize(event: Event): void {
    const target = event.target as HTMLInputElement;
    const size = +target.value;
    this.symbolSize = size;
    this.updateLayerStyle();
  }

  setTextColor(color: string): void {
    this.textColor = color;
  }

  setTextContent(event: Event): void {
    const target = event.target as HTMLInputElement;
    const content = target.value;
    this.textContent = content;
  }

  toggleTextFormatting(format: keyof TextFormatting): void {
    if (format === 'fontSize') return;
    this.textFormatting[format] = !this.textFormatting[format];
  }

  setFontSize(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const size = +target.value;
    this.textFormatting.fontSize = size;
  }

  private updateLayerStyle(): void {
    // Update existing features with new styles
    const features = this.vectorSource.getFeatures();
    features.forEach(feature => {
      const geometry = feature.getGeometry();
      if (geometry) {
        feature.setStyle(this.getFeatureStyle(geometry));
      }
    });
  }

  private getFeatureStyle(geometry: any): Style {
    const geometryType = geometry.getType();
    
    if (geometryType === 'Point') {
      return new Style({
        image: new Circle({
          radius: 5 * this.symbolSize,
          fill: new Fill({
            color: this.lineColor
          }),
          stroke: new Stroke({
            color: '#ffffff',
            width: 2
          })
        })
      });
    } else if (geometryType === 'LineString') {
      return new Style({
        stroke: new Stroke({
          color: this.lineColor,
          width: this.lineWidth
        })
      });
    } else if (geometryType === 'Polygon') {
      return new Style({
        fill: new Fill({
          color: 'rgba(0, 124, 191, 0.3)'
        }),
        stroke: new Stroke({
          color: this.lineColor,
          width: this.lineWidth
        })
      });
    }
    
    return this.getDefaultStyle();
  }

  private setupTextDrawing(): void {
    // Add click listener for text placement and node drawing
    const key = this.map.on('click', (evt) => {
      if (this.currentDrawingMode === DrawingMode.TEXT) {
        this.addTextFeature(evt.coordinate);
      } else if (this.isNodeDrawing) {
        this.handleMapClickForNodes(evt.coordinate);
      }
    });
    this.listenerKeys.push(key);
    console.log('Text drawing mode activated. Click on the map to place text.');
  }

  private clearTextDrawingListeners(): void {
    // Remove any text drawing related listeners
    this.listenerKeys = this.listenerKeys.filter(key => {
      // Keep only non-text drawing listeners
      return true;
    });
  }

  private addTextFeature(coordinate: number[]): void {
    if (!this.textContent || this.textContent.trim() === '') {
      console.log('No text content to add');
      return;
    }

    const feature = new Feature({
      geometry: new Point(coordinate),
      text: this.textContent,
      textStyle: {
        color: this.textColor,
        fontSize: this.textFormatting.fontSize,
        bold: this.textFormatting.bold,
        italic: this.textFormatting.italic,
        underline: this.textFormatting.underline
      }
    });

    feature.setStyle(this.getTextStyle());
    this.vectorSource.addFeature(feature);
    this.addToHistory();
    console.log('Text feature added:', this.textContent);
  }

  private getTextStyle(): Style {
    let fontStyle = '';
    if (this.textFormatting.bold) fontStyle += 'bold ';
    if (this.textFormatting.italic) fontStyle += 'italic ';
    
    return new Style({
      text: new Text({
        text: this.textContent,
        font: `${fontStyle}${this.textFormatting.fontSize}px Arial`,
        fill: new Fill({
          color: this.textColor
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        }),
        offsetY: -10
      })
    });
  }

  // Toolbar methods
  zoomIn(): void {
    const view = this.map.getView();
    const zoom = view.getZoom() || 0;
    view.animate({ zoom: zoom + 1, duration: 300 });
  }

  zoomOut(): void {
    const view = this.map.getView();
    const zoom = view.getZoom() || 0;
    view.animate({ zoom: zoom - 1, duration: 300 });
  }

  fitToExtent(): void {
    const extent = this.vectorSource.getExtent();
    if (extent && extent[0] !== Infinity) {
      this.map.getView().fit(extent, { duration: 500, padding: [20, 20, 20, 20] });
    }
  }

  goToExtent(): void {
    // Go to a predefined extent (Bhopal area)
    const view = this.map.getView();
    view.animate({
      center: fromLonLat([77.4146, 23.2599]),
      zoom: 10,
      duration: 1000
    });
  }

  pan(): void {
    this.setDrawingModeInternal(DrawingMode.SELECT);
  }

  select(): void {
    this.setDrawingModeInternal(DrawingMode.SELECT);
    this.modify.setActive(true);
    console.log('Select mode activated');
  }

  edit(): void {
    this.setDrawingModeInternal(DrawingMode.SELECT);
    this.modify.setActive(true);
    console.log('Edit mode activated');
  }

  delete(): void {
    const features = this.vectorSource.getFeatures();
    if (features.length > 0) {
      // Remove the last added feature
      this.vectorSource.removeFeature(features[features.length - 1]);
      this.addToHistory();
      console.log('Feature deleted');
    } else {
      console.log('No features to delete');
    }
  }

  // Measurement methods
  measureDistance(): void {
    this.setDrawingModeInternal(DrawingMode.LINE);
    this.setupDistanceMeasurement();
  }

  measureArea(): void {
    this.setDrawingModeInternal(DrawingMode.POLYGON);
    this.setupAreaMeasurement();
  }

  measureBearing(): void {
    // Implementation for bearing measurement
    console.log('Bearing measurement not implemented yet');
  }

  private setupDistanceMeasurement(): void {
    // Add distance measurement logic
    if (this.draw) {
      this.draw.on('drawend', (evt) => {
        const feature = evt.feature;
        const geometry = feature.getGeometry() as LineString;
        const length = getLength(geometry);
        const output = length > 100 ? 
          (Math.round(length / 1000 * 100) / 100) + ' km' :
          Math.round(length * 100) / 100 + ' m';
        
        this.measureTooltipElement.innerHTML = output;
        this.measureTooltip.setPosition(geometry.getLastCoordinate());
        
        // Add measurement to feature properties
        feature.set('measurement', output);
        feature.set('type', 'measurement');
        
        console.log('Distance measured:', output);
      });
    }
  }

  private setupAreaMeasurement(): void {
    // Add area measurement logic
    if (this.draw) {
      this.draw.on('drawend', (evt) => {
        const feature = evt.feature;
        const geometry = feature.getGeometry() as Polygon;
        const area = getArea(geometry);
        const output = area > 10000 ? 
          (Math.round(area / 1000000 * 100) / 100) + ' km²' :
          Math.round(area * 100) / 100 + ' m²';
        
        this.measureTooltipElement.innerHTML = output;
        const extent = geometry.getExtent();
        this.measureTooltip.setPosition(getCenter(extent));
        
        // Add measurement to feature properties
        feature.set('measurement', output);
        feature.set('type', 'measurement');
        
        console.log('Area measured:', output);
      });
    }
  }


  // History methods
  undo(): void {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      this.restoreFromHistory();
    }
  }

  redo(): void {
    if (this.historyIndex < this.history.length - 1) {
      this.historyIndex++;
      this.restoreFromHistory();
    }
  }

  clear(): void {
    this.vectorSource.clear();
    this.addToHistory();
    // Clear any active drawing mode
    this.setDrawingModeInternal(DrawingMode.SELECT);
  }

  private addToHistory(): void {
    const features = this.vectorSource.getFeatures().map(feature => {
      return {
        geometry: feature.getGeometry()?.clone(),
        style: feature.getStyle()
      };
    });
    
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(features);
    this.historyIndex = this.history.length - 1;
  }

  private restoreFromHistory(): void {
    if (this.history[this.historyIndex]) {
      this.vectorSource.clear();
      this.history[this.historyIndex].forEach((featureData: any) => {
        const feature = new Feature(featureData.geometry);
        if (featureData.style) {
          feature.setStyle(featureData.style);
        }
        this.vectorSource.addFeature(feature);
      });
    }
  }
  // File operations
  export(): void {
    const features = this.vectorSource.getFeatures();
    const geojson = {
      type: 'FeatureCollection',
      features: features.map(feature => {
        const geometry = feature.getGeometry();
        return {
          type: 'Feature',
          geometry: geometry ? {
            type: geometry.getType(),
            coordinates: (geometry as any).getCoordinates()
          } : null,
          properties: feature.getProperties()
        };
      })
    };

    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'map-export.geojson';
    link.click();
    URL.revokeObjectURL(url);
  }

  import(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const geojson = JSON.parse(e.target?.result as string);
          this.importGeoJSON(geojson);
        } catch (error) {
          console.error('Error parsing GeoJSON:', error);
        }
      };
      reader.readAsText(file);
    }
  }

  private importGeoJSON(geojson: any): void {
    // Implementation for importing GeoJSON
    console.log('Importing GeoJSON:', geojson);
  }

  print(): void {
    window.print();
  }

  fullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.mapContainer.nativeElement.requestFullscreen();
    }
  }

  showMiniMap(): void {
    // Implementation for mini map
    console.log('Mini map not implemented yet');
  }

  showHelp(): void {
    // Implementation for help
    console.log('Help not implemented yet');
  }

  // Project and settings
  onProjectChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedProject = target.value;
  }

  openSettings(): void {
    // Implementation for settings dialog
    console.log('Settings not implemented yet');
  }

  takeSnapshot(): void {
    // Implementation for taking snapshot
    console.log('Snapshot not implemented yet');
  }

  // Utility methods
  getCoordinatesString(): string {
    return `Lat: ${this.coordinates.lat.toFixed(6)}, Lon: ${this.coordinates.lon.toFixed(6)}`;
  }

  getScaleString(): string {
    return `Scale: ${this.scale}`;
  }

  getZoomString(): string {
    return `Zoom: ${this.zoom}`;
  }

  // File input trigger method
  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Segment file input trigger method
  triggerSegmentImport(): void {
    const fileInput = document.getElementById('segmentFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Enhanced Segment Management methods
  updateComments(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.comments = target.value;
  }

  updateNumberingType(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.numberingType = target.value;
  }

  updateSegmentPrefix(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.segmentPrefix = target.value;
    this.updateCurrentSegment();
  }

  updateSegmentSuffix(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.segmentSuffix = target.value;
    this.updateCurrentSegment();
  }

  addSegment(): void {
    if (this.isSegmentDrawing) {
      // Create segment from drawn geometry
      this.createSegmentFromDrawing();
    } else {
      // Create empty segment
      const newSegment: SegmentInfo = {
        id: this.generateSegmentId(),
        name: this.currentSegment,
        prefix: this.segmentPrefix,
        suffix: this.segmentSuffix,
        comments: this.comments,
        subSegments: [],
        properties: {
          createdDate: new Date(),
          modifiedDate: new Date()
        },
        isSelected: false,
        isVisible: true
      };
      
      this.segments.push(newSegment);
      this.currentSegmentIndex = this.segments.length - 1;
      this.updateCurrentSegment();
      this.comments = '';
      console.log('Empty segment created:', newSegment.name);
    }
  }

  addSubSegment(): void {
    if (this.segments.length > 0) {
      const currentSeg = this.segments[this.currentSegmentIndex];
      const newSubSegment: SegmentInfo = {
        id: this.generateSegmentId(),
        name: `${currentSeg.name}-${currentSeg.subSegments.length + 1}`,
        prefix: currentSeg.prefix,
        suffix: currentSeg.suffix,
        comments: this.comments,
        subSegments: [],
        properties: {
          createdDate: new Date(),
          modifiedDate: new Date()
        },
        isSelected: false,
        isVisible: true
      };
      
      currentSeg.subSegments.push(newSubSegment);
      this.comments = ''; // Clear comments after adding
      console.log('Sub-segment created:', newSubSegment.name);
    }
  }

  divideRoad(): void {
    // Implementation for dividing road into segments
    console.log('Divide road functionality not implemented yet');
  }

  resetNumbering(): void {
    this.segments = [];
    this.currentSegmentIndex = 0;
    this.segmentPrefix = 'SEG-';
    this.segmentSuffix = '';
    this.updateCurrentSegment();
  }

  private generateSegmentId(): string {
    return `seg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private updateCurrentSegment(): void {
    if (this.numberingType === 'auto') {
      const segmentNumber = this.segments.length + 1;
      const subSegmentLetter = this.segments.length > 0 && this.segments[this.currentSegmentIndex]?.subSegments.length > 0 
        ? String.fromCharCode(65 + this.segments[this.currentSegmentIndex].subSegments.length) 
        : 'A';
      this.currentSegment = `${this.segmentPrefix}${segmentNumber}-${subSegmentLetter}${this.segmentSuffix}`;
    } else {
      this.currentSegment = `${this.segmentPrefix}${this.segmentSuffix}`;
    }
  }

  // Segment drawing methods
  startSegmentDrawing(): void {
    this.isSegmentDrawing = true;
    this.currentSegmentPoints = [];
    
    // Clear any existing segment drawing listeners
    this.clearSegmentDrawingListeners();
    
    // Add click listener for segment drawing
    const key = this.map.on('click', (evt) => {
      if (this.isSegmentDrawing) {
        this.addSegmentPoint(evt.coordinate);
      }
    });
    this.listenerKeys.push(key);
    
    // Add double-click listener to finish segment
    const doubleClickKey = this.map.on('dblclick', (evt) => {
      if (this.isSegmentDrawing) {
        evt.preventDefault(); // Prevent map zoom on double-click
        this.finishSegmentDrawing();
      }
    });
    this.listenerKeys.push(doubleClickKey);
    
    // Add right-click listener to cancel segment drawing
    const rightClickKey = this.map.on('pointerdown' as any, (evt: any) => {
      if (this.isSegmentDrawing && evt.originalEvent.button === 2) {
        evt.preventDefault(); // Prevent context menu
        this.cancelSegmentDrawing();
      }
    });
    this.listenerKeys.push(rightClickKey);
    
    console.log('Segment drawing mode activated. Click on map to draw segment. Double-click to finish, right-click to cancel.');
  }

  stopSegmentDrawing(): void {
    this.isSegmentDrawing = false;
    this.currentSegmentPoints = [];
    this.clearSegmentDrawingListeners();
    console.log('Segment drawing mode deactivated.');
  }

  private addSegmentPoint(coordinate: number[]): void {
    this.currentSegmentPoints.push(coordinate);
    
    // Create temporary feature to show current drawing progress
    this.updateSegmentDrawingPreview();
    
    console.log(`Point ${this.currentSegmentPoints.length} added at:`, coordinate);
  }

  private updateSegmentDrawingPreview(): void {
    // Remove existing preview features
    this.clearSegmentDrawingPreview();
    
    if (this.currentSegmentPoints.length === 0) return;
    
    // Add point markers for each clicked point
    this.currentSegmentPoints.forEach((point, index) => {
      const pointFeature = new Feature(new Point(point));
      pointFeature.setStyle(new Style({
        image: new Circle({
          radius: 6,
          fill: new Fill({ color: '#ff6b35' }),
          stroke: new Stroke({ color: '#ffffff', width: 2 })
        }),
        text: new Text({
          text: (index + 1).toString(),
          font: '12px Arial',
          fill: new Fill({ color: '#ffffff' }),
          stroke: new Stroke({ color: '#000000', width: 2 })
        })
      }));
      pointFeature.set('isPreview', true);
      this.vectorSource.addFeature(pointFeature);
    });
    
    // Add line preview if we have at least 2 points
    if (this.currentSegmentPoints.length > 1) {
      const lineFeature = new Feature(new LineString(this.currentSegmentPoints));
      lineFeature.setStyle(new Style({
        stroke: new Stroke({
          color: '#ff6b35',
          width: 3,
          lineDash: [5, 5] // Dashed line for preview
        })
      }));
      lineFeature.set('isPreview', true);
      this.vectorSource.addFeature(lineFeature);
    }
    
    // Add polygon preview if we have at least 3 points and mode is polygon
    if (this.segmentDrawingMode === 'polygon' && this.currentSegmentPoints.length > 2) {
      const closedPoints = [...this.currentSegmentPoints, this.currentSegmentPoints[0]];
      const polygonFeature = new Feature(new Polygon([closedPoints]));
      polygonFeature.setStyle(new Style({
        stroke: new Stroke({
          color: '#ff6b35',
          width: 3,
          lineDash: [5, 5]
        }),
        fill: new Fill({
          color: 'rgba(255, 107, 53, 0.2)'
        })
      }));
      polygonFeature.set('isPreview', true);
      this.vectorSource.addFeature(polygonFeature);
    }
  }

  private clearSegmentDrawingPreview(): void {
    const features = this.vectorSource.getFeatures();
    const previewFeatures = features.filter(feature => feature.get('isPreview'));
    previewFeatures.forEach(feature => this.vectorSource.removeFeature(feature));
  }

  private finishSegmentDrawing(): void {
    if (this.currentSegmentPoints.length < 2) {
      console.log('Need at least 2 points to create a segment');
      return;
    }
    
    if (this.segmentDrawingMode === 'polygon' && this.currentSegmentPoints.length < 3) {
      console.log('Need at least 3 points to create a polygon segment');
      return;
    }
    
    // Create the segment from the drawn points
    this.createSegmentFromDrawing();
    
    // Clean up
    this.clearSegmentDrawingPreview();
    this.stopSegmentDrawing();
  }

  private cancelSegmentDrawing(): void {
    console.log('Segment drawing cancelled');
    this.clearSegmentDrawingPreview();
    this.stopSegmentDrawing();
  }

  private clearSegmentDrawingListeners(): void {
    // Remove segment drawing related listeners
    this.listenerKeys = this.listenerKeys.filter(key => {
      // Keep all listeners for now - in a more sophisticated implementation,
      // you'd want to track which listeners are for segment drawing specifically
      return true;
    });
  }

  private createSegmentFromDrawing(): void {
    if (this.currentSegmentPoints.length < 2) {
      console.log('Need at least 2 points to create a segment');
      return;
    }

    let geometry: any;
    if (this.segmentDrawingMode === 'line') {
      geometry = new LineString(this.currentSegmentPoints);
    } else {
      // Close polygon if needed
      if (this.currentSegmentPoints.length > 2) {
        const closedPoints = [...this.currentSegmentPoints, this.currentSegmentPoints[0]];
        geometry = new Polygon([closedPoints]);
      } else {
        console.log('Need at least 3 points to create a polygon segment');
        return;
      }
    }

    const newSegment: SegmentInfo = {
      id: this.generateSegmentId(),
      name: this.currentSegment,
      prefix: this.segmentPrefix,
      suffix: this.segmentSuffix,
      comments: this.comments,
      subSegments: [],
      geometry: geometry,
      properties: {
        length: this.segmentDrawingMode === 'line' ? getLength(geometry) : undefined,
        area: this.segmentDrawingMode === 'polygon' ? getArea(geometry) : undefined,
        startPoint: this.currentSegmentPoints[0],
        endPoint: this.currentSegmentPoints[this.currentSegmentPoints.length - 1],
        createdDate: new Date(),
        modifiedDate: new Date()
      },
      isSelected: false,
      isVisible: true
    };

    // Create feature and add to map
    const feature = new Feature(geometry);
    feature.set('segmentId', newSegment.id);
    feature.set('segmentName', newSegment.name);
    feature.setStyle(this.getSegmentStyle());
    this.vectorSource.addFeature(feature);

    this.segments.push(newSegment);
    this.currentSegmentIndex = this.segments.length - 1;
    this.updateCurrentSegment();
    this.comments = '';
    this.stopSegmentDrawing();
    
    console.log('Segment created from drawing:', newSegment.name);
  }

  private getSegmentStyle(): Style {
    return new Style({
      stroke: new Stroke({
        color: '#ff6b35',
        width: 4
      }),
      fill: new Fill({
        color: 'rgba(255, 107, 53, 0.2)'
      })
    });
  }

  // Segment management methods
  selectSegment(segment: SegmentInfo): void {
    // Deselect all segments
    this.segments.forEach(seg => seg.isSelected = false);
    this.segments.forEach(seg => seg.subSegments.forEach(sub => sub.isSelected = false));
    
    // Select the clicked segment
    segment.isSelected = true;
    this.selectedSegment = segment;
    
    // Zoom to segment if it has geometry
    if (segment.geometry) {
      const extent = segment.geometry.getExtent();
      this.map.getView().fit(extent, { duration: 500, padding: [20, 20, 20, 20] });
    }
    
    console.log('Segment selected:', segment.name);
  }

  deleteSegment(segment: SegmentInfo): void {
    // Remove from segments array
    const index = this.segments.findIndex(seg => seg.id === segment.id);
    if (index > -1) {
      this.segments.splice(index, 1);
    }
    
    // Remove from map
    const features = this.vectorSource.getFeatures();
    const featureToRemove = features.find(feature => feature.get('segmentId') === segment.id);
    if (featureToRemove) {
      this.vectorSource.removeFeature(featureToRemove);
    }
    
    // Update current segment index
    if (this.currentSegmentIndex >= this.segments.length) {
      this.currentSegmentIndex = Math.max(0, this.segments.length - 1);
    }
    
    this.updateCurrentSegment();
    console.log('Segment deleted:', segment.name);
  }

  toggleSegmentVisibility(segment: SegmentInfo): void {
    segment.isVisible = !segment.isVisible;
    
    // Update map feature visibility
    const features = this.vectorSource.getFeatures();
    const feature = features.find(f => f.get('segmentId') === segment.id);
    if (feature) {
      feature.setStyle(segment.isVisible ? this.getSegmentStyle() : null);
    }
    
    console.log('Segment visibility toggled:', segment.name, segment.isVisible);
  }

  editSegmentProperties(segment: SegmentInfo): void {
    // This would open a dialog or form to edit segment properties
    console.log('Edit segment properties:', segment.name);
    // Implementation would depend on your UI framework
  }

  exportSegments(): void {
    const segmentData = {
      segments: this.segments.map(segment => ({
        id: segment.id,
        name: segment.name,
        prefix: segment.prefix,
        suffix: segment.suffix,
        comments: segment.comments,
        properties: segment.properties,
        geometry: segment.geometry ? {
          type: segment.geometry.getType(),
          coordinates: (segment.geometry as any).getCoordinates()
        } : null,
        subSegments: segment.subSegments
      }))
    };

    const blob = new Blob([JSON.stringify(segmentData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'segments-export.json';
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('Segments exported');
  }

  importSegments(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const segmentData = JSON.parse(e.target?.result as string);
          this.loadSegmentsFromData(segmentData);
        } catch (error) {
          console.error('Error parsing segment data:', error);
        }
      };
      reader.readAsText(file);
    }
  }

  private loadSegmentsFromData(data: any): void {
    if (data.segments) {
      this.segments = data.segments.map((segmentData: any) => {
        let geometry = null;
        if (segmentData.geometry) {
          if (segmentData.geometry.type === 'LineString') {
            geometry = new LineString(segmentData.geometry.coordinates);
          } else if (segmentData.geometry.type === 'Polygon') {
            geometry = new Polygon(segmentData.geometry.coordinates);
          }
        }

        const segment: SegmentInfo = {
          id: segmentData.id,
          name: segmentData.name,
          prefix: segmentData.prefix,
          suffix: segmentData.suffix,
          comments: segmentData.comments,
          subSegments: segmentData.subSegments || [],
          geometry: geometry,
          properties: {
            ...segmentData.properties,
            createdDate: new Date(segmentData.properties.createdDate),
            modifiedDate: new Date(segmentData.properties.modifiedDate)
          },
          isSelected: false,
          isVisible: true
        };

        // Add to map if geometry exists
        if (geometry) {
          const feature = new Feature(geometry);
          feature.set('segmentId', segment.id);
          feature.set('segmentName', segment.name);
          feature.setStyle(this.getSegmentStyle());
          this.vectorSource.addFeature(feature);
        }

        return segment;
      });
      
      this.updateCurrentSegment();
      console.log('Segments imported:', this.segments.length);
    }
  }

  // ===== CONSTRUCTION MANAGEMENT METHODS =====

  // Toggle construction manager visibility
  toggleConstructionManager(): void {
    this.showConstructionManager = !this.showConstructionManager;
    if (this.showConstructionManager && this.selectedSegment) {
      this.selectedConstructionSegment = this.selectedSegment;
    }
  }

  // Initialize construction data for a segment
  initializeConstructionData(segment: SegmentInfo): void {
    if (!segment.construction) {
      segment.construction = {
        type: 'road',
        status: 'planning',
        priority: 'medium',
        estimatedCost: 0,
        actualCost: 0,
        estimatedDuration: 30,
        actualDuration: 0,
        progress: 0,
        materials: [],
        equipment: [],
        phases: [],
        nodes: [],
        qualityStandards: [],
        safetyRequirements: [],
        permits: [],
        inspections: [],
        risks: [],
        documents: []
      };
    }
  }

  // Add construction data to segment
  addConstructionToSegment(segment: SegmentInfo): void {
    this.initializeConstructionData(segment);
    if (segment.construction) {
      segment.construction.type = this.constructionForm.type as any;
      segment.construction.status = this.constructionForm.status as any;
      segment.construction.priority = this.constructionForm.priority as any;
      segment.construction.estimatedCost = this.constructionForm.estimatedCost;
      segment.construction.estimatedDuration = this.constructionForm.estimatedDuration;
      segment.construction.contractor = this.constructionForm.contractor;
      segment.construction.supervisor = this.constructionForm.supervisor;
      segment.construction.qualityInspector = this.constructionForm.qualityInspector;
      segment.construction.safetyOfficer = this.constructionForm.safetyOfficer;
      segment.construction.materials = this.constructionForm.materials.split(',').map(m => m.trim()).filter(m => m);
      segment.construction.equipment = this.constructionForm.equipment.split(',').map(e => e.trim()).filter(e => e);
      segment.construction.qualityStandards = this.constructionForm.qualityStandards.split(',').map(s => s.trim()).filter(s => s);
      segment.construction.safetyRequirements = this.constructionForm.safetyRequirements.split(',').map(s => s.trim()).filter(s => s);
      segment.construction.environmentalImpact = this.constructionForm.environmentalImpact;
      segment.construction.permits = this.constructionForm.permits.split(',').map(p => p.trim()).filter(p => p);
    }
    console.log('Construction data added to segment:', segment.name);
  }

  // Node Management Methods
  startNodeDrawing(): void {
    this.isNodeDrawing = true;
    console.log('Node drawing mode activated. Click on map to place node.');
  }

  stopNodeDrawing(): void {
    this.isNodeDrawing = false;
    console.log('Node drawing mode deactivated.');
  }

  addNodeToSegment(segment: SegmentInfo, coordinates: number[]): void {
    this.initializeConstructionData(segment);
    if (segment.construction) {
      const newNode: ConstructionNode = {
        id: this.generateNodeId(),
        name: this.newNodeForm.name || `${this.newNodeForm.type}-${segment.construction.nodes.length + 1}`,
        type: this.newNodeForm.type as any,
        coordinates: coordinates,
        properties: {
          status: 'planned',
          priority: this.newNodeForm.priority as any,
          assignedTo: this.newNodeForm.assignedTo,
          estimatedDuration: this.newNodeForm.estimatedDuration,
          notes: this.newNodeForm.notes,
          materials: [],
          equipment: [],
          attachments: [],
          qualityCheck: false,
          safetyCheck: false,
          createdDate: new Date(),
          modifiedDate: new Date()
        },
        isSelected: false,
        isVisible: true
      };

      segment.construction.nodes.push(newNode);
      this.addNodeToMap(newNode);
      this.resetNewNodeForm();
      console.log('Node added to segment:', newNode.name);
    }
  }

  private generateNodeId(): string {
    return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private addNodeToMap(node: ConstructionNode): void {
    const point = new Point(node.coordinates);
    const feature = new Feature(point);
    feature.set('nodeId', node.id);
    feature.set('nodeName', node.name);
    feature.set('nodeType', node.type);
    feature.setStyle(this.getNodeStyle(node));
    this.vectorSource.addFeature(feature);
  }

  private getNodeStyle(node: ConstructionNode): Style {
    const colors = {
      'start': '#28a745',
      'end': '#dc3545',
      'milestone': '#007bff',
      'checkpoint': '#ffc107',
      'junction': '#6f42c1',
      'bridge': '#17a2b8',
      'tunnel': '#6c757d',
      'custom': '#fd7e14'
    };

    return new Style({
      image: new Circle({
        radius: 8,
        fill: new Fill({
          color: colors[node.type] || '#007bff'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        })
      }),
      text: new Text({
        text: node.name,
        font: '12px Arial',
        fill: new Fill({
          color: '#000000'
        }),
        stroke: new Stroke({
          color: '#ffffff',
          width: 2
        }),
        offsetY: -15
      })
    });
  }

  selectNode(node: ConstructionNode): void {
    // Deselect all nodes
    this.segments.forEach(seg => {
      if (seg.construction?.nodes) {
        seg.construction.nodes.forEach(n => n.isSelected = false);
      }
    });
    
    // Select the clicked node
    node.isSelected = true;
    this.selectedNode = node;
    
    // Zoom to node
    const point = new Point(node.coordinates);
    const extent = point.getExtent();
    this.map.getView().fit(extent, { duration: 500, padding: [50, 50, 50, 50] });
    
    console.log('Node selected:', node.name);
  }

  deleteNode(node: ConstructionNode, segment: SegmentInfo): void {
    if (segment.construction) {
      const index = segment.construction.nodes.findIndex(n => n.id === node.id);
      if (index > -1) {
        segment.construction.nodes.splice(index, 1);
      }
    }
    
    // Remove from map
    const features = this.vectorSource.getFeatures();
    const featureToRemove = features.find(feature => feature.get('nodeId') === node.id);
    if (featureToRemove) {
      this.vectorSource.removeFeature(featureToRemove);
    }
    
    console.log('Node deleted:', node.name);
  }

  // Phase Management Methods
  addPhaseToSegment(segment: SegmentInfo): void {
    this.initializeConstructionData(segment);
    if (segment.construction) {
      const newPhase: ConstructionPhase = {
        id: this.generatePhaseId(),
        name: this.newPhaseForm.name,
        description: this.newPhaseForm.description,
        order: segment.construction.phases.length + 1,
        status: 'pending',
        estimatedCost: this.newPhaseForm.estimatedCost,
        estimatedDuration: this.newPhaseForm.estimatedDuration,
        progress: 0,
        dependencies: [],
        deliverables: this.newPhaseForm.deliverables.split(',').map(d => d.trim()).filter(d => d),
        qualityStandards: this.newPhaseForm.qualityStandards.split(',').map(s => s.trim()).filter(s => s),
        safetyRequirements: this.newPhaseForm.safetyRequirements.split(',').map(s => s.trim()).filter(s => s)
      };

      segment.construction.phases.push(newPhase);
      this.resetNewPhaseForm();
      console.log('Phase added to segment:', newPhase.name);
    }
  }

  private generatePhaseId(): string {
    return `phase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  selectPhase(phase: ConstructionPhase): void {
    this.selectedPhase = phase;
    console.log('Phase selected:', phase.name);
  }

  deletePhase(phase: ConstructionPhase, segment: SegmentInfo): void {
    if (segment.construction) {
      const index = segment.construction.phases.findIndex(p => p.id === phase.id);
      if (index > -1) {
        segment.construction.phases.splice(index, 1);
        // Reorder remaining phases
        segment.construction.phases.forEach((p, i) => p.order = i + 1);
      }
    }
    
    console.log('Phase deleted:', phase.name);
  }

  // Utility Methods
  private resetNewNodeForm(): void {
    this.newNodeForm = {
      name: '',
      type: 'milestone',
      notes: '',
      assignedTo: '',
      estimatedDuration: 1,
      priority: 'medium'
    };
  }

  private resetNewPhaseForm(): void {
    this.newPhaseForm = {
      name: '',
      description: '',
      estimatedCost: 0,
      estimatedDuration: 1,
      deliverables: '',
      qualityStandards: '',
      safetyRequirements: ''
    };
  }

  // Calculate construction progress
  calculateConstructionProgress(segment: SegmentInfo): number {
    if (!segment.construction || segment.construction.phases.length === 0) {
      return 0;
    }
    
    const totalProgress = segment.construction.phases.reduce((sum, phase) => sum + phase.progress, 0);
    return Math.round(totalProgress / segment.construction.phases.length);
  }

  // Get construction status color
  getConstructionStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      'planning': '#6c757d',
      'design': '#17a2b8',
      'preparation': '#ffc107',
      'construction': '#007bff',
      'testing': '#fd7e14',
      'completed': '#28a745',
      'maintenance': '#6f42c1'
    };
    return colors[status] || '#6c757d';
  }

  // Get priority color
  getPriorityColor(priority: string): string {
    const colors: { [key: string]: string } = {
      'low': '#28a745',
      'medium': '#ffc107',
      'high': '#fd7e14',
      'critical': '#dc3545'
    };
    return colors[priority] || '#6c757d';
  }

  // Get node type color
  getNodeTypeColor(nodeType: string): string {
    const colors: { [key: string]: string } = {
      'start': '#28a745',
      'end': '#dc3545',
      'milestone': '#007bff',
      'checkpoint': '#ffc107',
      'junction': '#6f42c1',
      'bridge': '#17a2b8',
      'tunnel': '#6c757d',
      'custom': '#fd7e14'
    };
    return colors[nodeType] || '#007bff';
  }

  // Get phase status color
  getPhaseStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      'pending': '#6c757d',
      'in-progress': '#007bff',
      'completed': '#28a745',
      'on-hold': '#ffc107'
    };
    return colors[status] || '#6c757d';
  }

  // Handle map click for node drawing
  handleMapClickForNodes(coordinate: number[]): void {
    if (this.isNodeDrawing && this.selectedConstructionSegment) {
      this.addNodeToSegment(this.selectedConstructionSegment, coordinate);
      this.stopNodeDrawing();
    }
  }
}
